import type { RefObject } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IVtsExhibitor } from '../../Admin/Exhibitors/Exhibitors';
import type { BoothPalette } from '../brandPalette';
import type { BoothTrack } from '../panels';
import MediaSlot from './MediaSlot/MediaSlot';
import styles from '../BoothOverlay.module.scss?module';

interface FasciaProps {
  exhibitor: IVtsExhibitor;
  /** Number of products in the booth line; the Assortment badge once it is known. */
  productsCount?: number | null;
  /** Colours reach the fascia through the --booth-* variables of the sheet. */
  palette: BoothPalette;
  /** Id of the brand name heading, labels the dialog. */
  titleId: string;
  onClose: () => void;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  track: BoothTrack;
}

/**
 * The brand band of the booth, modelled on the fascia of a real trade show stand: brand
 * colour, logo, brand name in capitals, booth line, and a 16:9 media slot.
 */
export default function Fascia({
  exhibitor,
  productsCount,
  titleId,
  onClose,
  closeButtonRef,
  track,
}: FasciaProps) {
  const meta = boothMeta(exhibitor, productsCount);

  return (
    <div className={styles.fascia}>
      <button
        ref={closeButtonRef}
        type="button"
        className={styles.close}
        aria-label="Close booth"
        onClick={onClose}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <div className={styles.fasciaText}>
        {exhibitor.logo_url && (
          <div
            className={
              exhibitor.full_color
                ? styles.fasciaLogoBare
                : styles.fasciaLogoPlate
            }
          >
            <img
              src={exhibitor.logo_url}
              alt=""
              className={styles.fasciaLogo}
            />
          </div>
        )}
        <h2 id={titleId} className={styles.fasciaName}>
          {exhibitor.name}
        </h2>
        {meta && <p className={styles.fasciaMeta}>{meta}</p>}
      </div>
      <MediaSlot exhibitor={exhibitor} track={track} />
    </div>
  );
}

/** "Premium booth · Booth 6 · 13 products"; parts without data are left out. */
function boothMeta(
  exhibitor: IVtsExhibitor,
  productsCount?: number | null
): string {
  const parts: string[] = [];

  if (exhibitor.package === 'premium') {
    parts.push('Premium booth');
  } else if (exhibitor.package === 'basic') {
    parts.push('Basic booth');
  }
  if (exhibitor.boothNumberB2b) {
    parts.push(`Booth ${exhibitor.boothNumberB2b}`);
  }
  if (productsCount) {
    const count = new Intl.NumberFormat('en-GB').format(productsCount);
    parts.push(`${count} ${productsCount === 1 ? 'product' : 'products'}`);
  }

  return parts.join(' · ');
}
